import axios from "axios";

/**
 * ClinicalDataService
 * Centralized in-memory cache and request deduplication manager for clinical records.
 *
 * Prevents N+1 query storms and duplicate HTTP calls by:
 * 1. Deduplicating concurrent in-flight requests for the same recipient-donor pair.
 * 2. Caching fetched clinical records in memory with a TTL.
 * 3. Providing pre-warming mechanisms when batch data is loaded.
 */

class ClinicalDataService {
  constructor() {
    // In-memory cache for clinical pairs: key = `${recipientId}_${donorId}`
    this.pairCache = new Map();
    // Map of active in-flight promises to deduplicate simultaneous requests
    this.inFlightRequests = new Map();
    // Cache TTL in ms (default: 5 minutes)
    this.ttl = 5 * 60 * 1000;
  }

  _getKey(recipientId, donorId) {
    return `${recipientId || "none"}_${donorId || "none"}`;
  }

  /**
   * Directly sets/pre-warms the cache for a clinical pair (e.g., from batch crossmatch data).
   */
  setCachedPair(recipientId, donorId, clinicalPairData) {
    if (!recipientId || !donorId || !clinicalPairData) return;
    const key = this._getKey(recipientId, donorId);
    this.pairCache.set(key, {
      data: clinicalPairData,
      timestamp: Date.now(),
    });
  }

  /**
   * Retrieves a cached pair if present and unexpired.
   */
  getCachedPair(recipientId, donorId) {
    const key = this._getKey(recipientId, donorId);
    const cached = this.pairCache.get(key);
    if (!cached) return null;
    if (Date.now() - cached.timestamp > this.ttl) {
      this.pairCache.delete(key);
      return null;
    }
    return cached.data;
  }

  /**
   * Fetches a clinical pair with request deduplication and in-memory caching.
   * If 5 components request the exact same pair simultaneously, only 1 network request is sent.
   */
  async fetchClinicalPair(recipientId, donorId, token) {
    if (!recipientId || !donorId) {
      return null;
    }

    const key = this._getKey(recipientId, donorId);

    // 1. Check in-memory cache first
    const cached = this.getCachedPair(recipientId, donorId);
    if (cached) {
      return cached;
    }

    // 2. Check if a request for this exact pair is already in-flight
    if (this.inFlightRequests.has(key)) {
      return this.inFlightRequests.get(key);
    }

    // 3. Dispatch new network request and register the in-flight promise
    const requestPromise = (async () => {
      try {
        const userToken = token || localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api";
        const response = await axios.post(
          `${apiUrl}/clinical/clinical-pair/`,
          { recipient_id: recipientId, donor_id: donorId },
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }
        );

        const data = response.data || null;
        if (data) {
          this.setCachedPair(recipientId, donorId, data);
        }
        return data;
      } catch (err) {
        console.warn(`[ClinicalDataService] Failed to fetch clinical pair ${key}:`, err.message);
        return null;
      } finally {
        // Remove from in-flight map once settled
        this.inFlightRequests.delete(key);
      }
    })();

    this.inFlightRequests.set(key, requestPromise);
    return requestPromise;
  }

  /**
   * Clears the in-memory cache (e.g., upon logout).
   */
  clearCache() {
    this.pairCache.clear();
    this.inFlightRequests.clear();
  }
}

export const clinicalDataService = new ClinicalDataService();
export default clinicalDataService;
